<template>
  <div v-if="isViewReady" class="listAllAds">
    <hl-ModalV2
      v-if="isAdModalVisible"
      title="Opprett annonse"
      titleIcon="budicon-announcement"
      headerSupportingText="Vi gjenbruker data fra leieobjektet, og vi foreslår automatisk en annonsetekst basert på boligens kvaliteter og egenskaper. Om du tidligere har laget en annonse hos oss kan du gjenbruke denne."
      is-closable
      @close="isAdModalVisible = false"
    >
      <hr />

      <div class="listAllAds__modalInput mt-3">
        Utleierprofil
        <select v-model="adModalSelectedLandlordProfileId" @change="adModalSelectedRentalObjectId = undefined">
          <option v-if="landlordProfiles.length > 1" :value="undefined" disabled>Velg</option>
          <option v-for="option in landlordProfileOptions" :key="option.value" :value="option.value">
            {{ option.name }}
          </option>
        </select>
      </div>

      <div class="listAllAds__modalInput mt-3">
        Leieobjekt
        <select
          :value="adModalSelectedRentalObjectId"
          @change="setAdModalSelectedRentalObjectId($event.target.value)"
          :disabled="!adModalSelectedLandlordProfileId"
        >
          <option :value="undefined" disabled selected>Velg</option>
          <option v-for="option in adModalRentalObjectOptions" :key="option.value" :value="option.value">
            {{ option.name }}
          </option>
        </select>
      </div>

      <hr class="mt-4" />

      <p v-if="adModalIsRentalObjectCommercial" class="mt-3">
        Det er ikke mulig å annonsere <strong>Næringseiendom</strong>. Hvis du ønsker
        å annonsere dette leieobjektet må det oppdateres til en annen boligtype.
      </p>

      <ReuseAdCard
        v-if="adModalSelectedRentalObjectId && !adModalIsRentalObjectCommercial"
        :landlord-id="adModalSelectedLandlordProfileId"
        :rental-object-id="adModalSelectedRentalObjectId"
        :is-loading="isReuseAdCardLoading"
        :ad="reuseAdCardData"
        class="mt-3"
      />

      <template v-slot:buttons>
        <hl-Button variant="secondary" outline class="font-weight-bold" @click="isAdModalVisible = false">
          Avbryt
        </hl-Button>

        <hl-Button
          :href="pubomaticAppCreateAdLink"
          target="_blank"
          rel="noopener"
          outline
          :disabled="!adModalSelectedRentalObjectId || adModalIsRentalObjectCommercial"
          class="font-weight-bold"
        >
          Opprett annonse
        </hl-Button>
      </template>
    </hl-ModalV2>

    <hl-Modal
      v-if="isVerificationModalVisible"
      class="text-center"
      title="BankID verifisering kreves"
      iconClass="budicon-announcement text-primary"
    >
      <p>
        Dette hjelper oss med å holde plattformen trygg og forhindre misbruk. Selv om du har verifisert deg tidligere,
        må vi bekrefte identiteten din ved hver publisering. Trykk på knappen under for å gå videre til verifisering.
      </p>

      <hl-Button class="mt-2" @click="createIdentificationSession"> Verifiser bruker </hl-Button>
    </hl-Modal>

    <hl-Modal
      v-if="isErrorModalVisible"
      class="text-center"
      title="En feil oppstod"
      iconClass="budicon-alert text-danger"
    >
      <p>
        {{ errorMessage }}
      </p>

      <div class="d-flex justify-content-center mt-5">
        <hl-Button @click="retryFinnAdPublish"> Forsøk igjen </hl-Button>
        <hl-Button variant="secondary" outline class="ml-3" @click="isErrorModalVisible = false"> Avbryt </hl-Button>
      </div>
    </hl-Modal>

    <hl-ModalV2
      v-if="isFinnPostingModalVisible"
      title="Publiser til FINN"
      title-icon="budicon-alert-sign"
      is-closable
      @close="isFinnPostingModalVisible = false"
    >
      <p>
        Ved å velge å publisere annonsen til FINN.no vil det faktureres <hl-Currency :amount="$options.FINN_PRICE" />.
        Dette vil være inkludert i neste faktura du mottar fra Husleie.no.
      </p>
      <hl-Checkbox v-model="areFinnTermsAgreed" size="sm">
        Jeg aksepterer FINNs
        <a
          href="https://hjelpesenter.finn.no/hc/no/articles/304234-Annonseregler-for-FINN-no"
          target="_blank"
          rel="noopener"
          class="text-primary"
        >
          annonseregler
        </a>
      </hl-Checkbox>

      <template #buttons>
        <hl-Button variant="primary" size="sm" :disabled="!areFinnTermsAgreed || isPublishingAd" @click="publishAd">
          Publiser
        </hl-Button>
      </template>
    </hl-ModalV2>

    <div class="listAllAds__headerSection">
      <div class="listAllAds__headerSection__content">
        <div class="listAllAds__headerSection__content__title">
          <h1>Utleieannonser</h1>
          <p>
            Her finner du en oversikt over alle dine aktive og utløpte annonser – alt samlet på ett sted. Når du klikker
            på <strong>opprett annonse</strong> vil du få muligheten til å gjenbruke tidligere annonser opprettet på
            leieobjektet.
          </p>
        </div>

        <div class="listAllAds__headerSection__content__buttons">
          <hl-Button
            href="/app/tenant-profiles"
            target="_blank"
            rel="noopener"
            variant="secondary"
          >
            <strong>Finn leietakere</strong>
          </hl-Button>

          <hl-Button @click="isAdModalVisible = true">
            <strong>Opprett annonse</strong>
          </hl-Button>
        </div>
      </div>
    </div>

    <div class="listAllAds__content">
      <div class="listAllAds__content__block">
        <div class="listAllAds__content__block__inputs">
          <hl-ButtonGroup v-model="filter" :buttons="filterButtons" />

          <select v-model="selectedLandlordProfile" @change="selectedRentalObject = 'all'">
            <option value="all">Alle utleierprofiler</option>
            <option v-for="option in landlordProfileOptions" :key="option.value" :value="option.value">
              {{ option.name }}
            </option>
          </select>

          <select v-model="selectedRentalObject" :disabled="selectedLandlordProfile === 'all'">
            <option value="all">Alle leieobjekter</option>
            <option v-for="option in rentalObjectOptions" :key="option.value" :value="option.value">
              {{ option.name }}
            </option>
          </select>
        </div>

        <div v-if="landlordRentalObjectAds.length" class="mt-5">
          <RentalObjectAd
            v-for="letting in rentalObjectAdsPaginated"
            :key="letting.id"
            :iM="letting.iM"
            :landlordId="letting.status.landlordId"
            :letting="letting"
            :isCompactDesign="true"
            class="mb-4"
            @publish-to-finn="handleFinnAdPublish"
          />

          <div class="listAllAds__content__pagination">
            <!-- Div used to align the second item `Side` in the center -->
            <div></div>

            <span>Side {{ page }} av {{ totalAdPages }}</span>

            <div class="d-flex justify-content-end">
              <hl-Button v-if="page > 1" variant="secondary" outline class="mr-2" @click="page--"> Forrige </hl-Button>

              <hl-Button v-if="page < totalAdPages" variant="secondary" outline @click="page++"> Neste </hl-Button>
            </div>
          </div>
        </div>

        <div v-else class="listAllAds__content__block__noAds">
          <img src="@/assets/empty-ads.svg" />

          <h4 v-if="filter === 'active'" class="mt-3">Du har ingen aktive annonser</h4>

          <h4 v-else-if="filter === 'expired'" class="mt-3">Du har ingen utløpte annonser</h4>

          <h4 v-else class="mt-3">Du har ingen annonser</h4>

          <hl-Button class="mt-4 font-weight-bold" @click="isAdModalVisible = true">
            <div class="d-flex align-items-center">
              <Icon :name="IconName.InterfacePlus" :size="IconSize.Small" class="text-white" />
              <span class="ml-2"> Opprett utleieannonse </span>
            </div>
          </hl-Button>
        </div>
      </div>

      <div v-if="!landlordRentalObjectAds.length" class="listAllAds__content__block mt-5">
        <h4>Finn de beste og tryggeste leietakerne</h4>

        <p class="text-gray2">Opprett gratis utleieannonse!</p>

        <div v-for="point in adKeyPoints" :key="point.title" class="listAllAds__content__block__adKeyPoints">
          <Icon :name="IconName.InterfaceCheckRounded" />
          <div>
            <span>{{ point.title }}</span>

            <p class="text-gray2">{{ point.text }}</p>
          </div>
        </div>

        <hl-Button
          variant="secondary"
          outline
          href="https://www.husleie.no/utleier/annonser-til-finn-no-fra-minhusleie/"
          target="_blank"
          rel="noopener"
          class="mt-2"
        >
          Les mer
        </hl-Button>
      </div>
    </div>
  </div>
  <hl-Modal v-else class="text-center" title="Siden laster">
    <hl-LoaderDots />
  </hl-Modal>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { flattenDeep } from 'lodash';
import ModalV2 from '@modules/husleie-framework/src/components/modals/ModalV2.vue';
import RentalObjectAd from '@/components/detailedRentalObject/interestManagement/RentalObjectAd.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import ButtonGroup from '@modules/husleie-framework/src/components/buttons/ButtonGroup.vue';
import Icon, { IconName, IconSize } from '@modules/design-system/components/Icon';
import Tag from '@modules/husleie-framework/src/components/forms/inputs/tags/Tag.vue';
import HlDate from '@modules/husleie-framework/src/components/time/Date.vue';
import ActionsMenu from '@/components/UI/ActionsMenu';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import ReuseAdCard from '@/components/ReuseAdCard.vue';
import {
  LandlordApi,
  RentalObjectApi,
  InterestManagementApi,
  LettingApi,
  IdentificationApi
} from '@modules/husleieapi2';
import { FINN_PRICE } from '@modules/husleie-framework/src/constants.ts';

export default Vue.extend({
  name: 'ListAllAds',
  data() {
    return {
      isViewReady: false,
      filter: 'active',
      landlordProfiles: [],
      isAdModalVisible: false,
      adModalSelectedLandlordProfileId: undefined,
      adModalSelectedRentalObjectId: undefined,
      selectedLandlordProfile: 'all',
      selectedRentalObject: 'all',
      page: 1,
      identificationId: undefined,
      isVerificationModalVisible: false,
      isFinnPostingModalVisible: false,
      isErrorModalVisible: false,
      errorMessage: '',
      areFinnTermsAgreed: false,
      isPublishingAd: false,
      finnPublishLettingId: undefined,
      env: process.env.NODE_ENV,
      reuseAdCardData: undefined,
      isReuseAdCardLoading: false,
      IconName,
      IconSize,
    };
  },
  components: {
    'hl-Button': Button,
    'hl-ButtonGroup': ButtonGroup,
    'hl-Tag': Tag,
    'hl-ModalV2': ModalV2,
    'hl-Date': HlDate,
    RentalObjectAd,
    Icon,
    ActionsMenu,
    ReuseAdCard,
  },
  FINN_PRICE,
  ADS_PER_PAGE: 4,
  watch: {
    landlordRentalObjectAds: {
      handler(value) {
        if (this.isViewReady && value) {
          this.page = 1;
        }
      },
    },
  },
  computed: {
    ...mapGetters(['getLandlordProfilesMeta']),

    filterButtons() {
      return [
        {
          value: 'active',
          text: 'Aktive',
        },
        {
          value: 'expired',
          text: 'Utløpte',
        },
        {
          value: 'all',
          text: 'Alle',
        },
      ];
    },
    landlordProfileOptions() {
      return this.landlordProfiles.map(landlord => ({
        value: landlord.id,
        name: landlord.fullName,
      }));
    },
    rentalObjectOptions() {
      if (this.selectedLandlordProfile === 'all') {
        return [];
      }

      return this.landlordRentalObjects(this.selectedLandlordProfile).map(ro => ({
        value: ro.id,
        name: ro.name,
      }));
    },
    adModalRentalObjectOptions() {
      if (!this.adModalSelectedLandlordProfileId) {
        return [];
      }

      return this.landlordRentalObjects(this.adModalSelectedLandlordProfileId).map(ro => ({
        value: ro.id,
        name: ro.name,
      }));
    },
    adKeyPoints() {
      return [
        {
          title: 'Velg annonsekanal',
          text: 'Vi har gjort det enkelt å velge hvor annonsene dine skal vises',
        },
        {
          title: 'Sett korrekt leiepris',
          text: 'Nå får du automatisk forslag til leiepris når du oppretter en utleieannonse',
        },
        {
          title: 'Administrer interessenter',
          text: 'Når annonsen er publisert, får du en enkel oversikt over alle som har vist interesse for utleieannonsen',
        },
      ];
    },
    landlordRentalObjectAds() {
      let ads = [];

      if (this.selectedLandlordProfile === 'all') {
        ads = flattenDeep(
          this.landlordProfiles.map(profile => {
            return profile.rentalObjects.map(ro => ro.ads);
          })
        );
      } else {
        const landlordProfile = this.landlordProfiles.find(
          profile => profile.id === parseInt(this.selectedLandlordProfile)
        );

        if (this.selectedRentalObject === 'all') {
          ads = landlordProfile.rentalObjects.map(ro => ro.ads).flat();
        } else {
          const rentalObject = landlordProfile.rentalObjects.find(ro => ro.id === parseInt(this.selectedRentalObject));
          ads = rentalObject.ads;
        }
      }

      if ([undefined, 'all'].includes(this.filter)) {
        return ads;
      } else {
        return ads.filter(ad => ad.status.status === this.filter);
      }
    },
    adModalSelectedRentalObject() {
      if (!this.adModalSelectedLandlordProfileId || !this.adModalSelectedRentalObjectId) {
        return undefined;
      }

      const landlordProfile = this.landlordProfiles.find(
        profile => profile.id === parseInt(this.adModalSelectedLandlordProfileId)
      );

      if (!landlordProfile) {
        return undefined;
      }

      return landlordProfile.rentalObjects.find(
        ro => ro.id === parseInt(this.adModalSelectedRentalObjectId)
      );
    },
    adModalSelectedRentalObjectAd() {
      return this.adModalSelectedRentalObject?.ads[0];
    },
    adModalIsRentalObjectCommercial() {
      return this.adModalSelectedRentalObject?.type === 'commercial';
    },
    totalAdPages() {
      return Math.ceil(this.landlordRentalObjectAds.length / this.$options.ADS_PER_PAGE);
    },
    rentalObjectAdsPaginated() {
      const start = (this.page - 1) * this.$options.ADS_PER_PAGE;

      return this.landlordRentalObjectAds.slice(start, start + this.$options.ADS_PER_PAGE);
    },
    pubomaticAppCreateAdLink() {
      if (!this.adModalSelectedRentalObjectId) {
        return undefined;
      }

      return RedirectOperations.getURL(AppRedirect.AD_CREATE, {
        landlordId: this.adModalSelectedLandlordProfileId,
        rentalObjectId: this.adModalSelectedRentalObjectId,
      });
    },
  },
  methods: {
    ...mapActions(['publishLetting']),

    landlordRentalObjects(landlordId) {
      return this.landlordProfiles.find(item => item.id === parseInt(landlordId))?.rentalObjects || [];
    },
    imageUrl(ad) {
      return ad.media?.length ? ad.media[0].mediaLinks[0].url : undefined;
    },
    findLandlordIdByAdId(adId) {
      for (const profile of this.landlordProfiles) {
        for (const ro of profile.rentalObjects) {
          if (ro.ads.some(ad => ad.id === adId)) {
            return profile.id;
          }
        }
      }

      return false;
    },
    async getLandlordRentalObjects(landlordId) {
      const api = new RentalObjectApi();

      return await api.getLandlordRentalObjects(landlordId, {
        query: {
          condensed: true,
        },
      });
    },
    async getLandlordInterestManagements(landlordId, rentalObjectId) {
      const api = new InterestManagementApi();

      return await api.getLandlordInterestManagements(landlordId, {
        query: {
          rentalObjectId: rentalObjectId,
        },
      });
    },
    async getLetting(landlordId, lettingId) {
      const api = new LettingApi();

      return await api.getLetting(landlordId, lettingId);
    },
    async getPageData() {
      // Might be duplicate ads through unique interest managements.
      const adIds = [];

      const promises = this.getLandlordProfilesMeta.map(async profile => {
        let rentalObjects = await this.getLandlordRentalObjects(profile.id);

        rentalObjects = rentalObjects.map(async ro => {
          const ims = (await this.getLandlordInterestManagements(profile.id, ro.id)) || [];
          let ads = [];

          if (ims && ims.length) {
            ads = ims.map(async im => {
              if (adIds.includes(im.ad.id)) {
                return undefined;
              }

              adIds.push(im.ad.id);

              // Using a try catch block here for `getLetting` as some ads might have data
              // quality issues, leading to a 500 openapi error.
              try {
                const letting = await this.getLetting(profile.id, im.ad.id);

                return {
                  iM: im,
                  ...letting,
                };
              } catch {
                return undefined;
              }
            });
            ads = (await Promise.all(ads)).filter(ad => ad);
          }

          const postalZipcode =
            ro.address.postalZipcode.charAt(0).toUpperCase() + ro.address.postalZipcode.toLowerCase().slice(1);

          return {
            ...ro,
            address: {
              ...ro.address,
              postalZipcode,
            },
            ads,
          };
        });

        return {
          id: profile.id,
          fullName: profile.fullName,
          rentalObjects: await Promise.all(rentalObjects),
        };
      });

      this.landlordProfiles = await Promise.all(promises);

      if (this.landlordProfiles.length === 1) {
        this.adModalSelectedLandlordProfileId = this.landlordProfiles[0].id;
      }
    },
    handlePageParameter() {
      if (!this.$route.query.page) {
        return;
      }

      const pageParameter = parseInt(this.$route.query.page);
      if (pageParameter && pageParameter <= this.totalAdPages) {
        this.page = pageParameter;
      }
    },
    async createIdentificationSession() {
      const api = new IdentificationApi();
      const res = await api.createIdentificationSession({
        body: {
          path: window.location.pathname,
        },
      });

      if (!res.url) {
        throw new Error('Missing field `url` from `createIdentificationSession` response');
      }

      window.localStorage.setItem('rentalObjectFinnPublishLettingId', this.finnPublishLettingId);
      window.location.href = res.url;
    },
    async handleFinnAdPublish(lettingId) {
      this.finnPublishLettingId = lettingId;

      if (this.identificationId) {
        this.isFinnPostingModalVisible = true;
      } else {
        this.isVerificationModalVisible = true;
      }
    },
    retryFinnAdPublish() {
      this.isErrorModalVisible = false;

      if (this.identificationId) {
        this.isFinnPostingModalVisible = true;
      } else {
        this.isVerificationModalVisible = true;
      }
    },
    async publishAd() {
      this.isPublishingAd = true;

      try {
        await this.publishLetting({
          identificationId: this.identificationId,
          landlordId: this.findLandlordIdByAdId(this.finnPublishLettingId),
          lettingId: this.finnPublishLettingId,
          name: 'finn',
        });
        window.localStorage.removeItem('rentalObjectFinnPublishLettingId');
        window.location.href = window.location.pathname;
      } catch (error) {
        this.isFinnPostingModalVisible = false;
        this.isErrorModalVisible = true;
        this.errorMessage = 'Publisering av annonsen feilet. Forsøk igjen om noen få minutter';
      } finally {
        this.isPublishingAd = false;
      }
    },
    async handleIdentificationAttempt() {
      const { externalReference } = this.$route.query;

      if (!externalReference) {
        return;
      }

      try {
        this.isLoading = true;

        const api = new IdentificationApi();
        const res = await api.getIdentificationSessionResult(externalReference);

        if (res.status === 'SUCCESS') {
          this.identificationId = externalReference;
          this.finnPublishLettingId = window.localStorage.getItem('rentalObjectFinnPublishLettingId');

          this.isLoading = false;
          this.isFinnPostingModalVisible = true;
        } else {
          this.isLoading = false;
          this.isErrorModalVisible = true;
          this.errorMessage = 'BankID verifisering ble avbrutt. Vennligst forsøk igjen.';
        }
      } catch {
        this.isLoading = false;
        this.isErrorModalVisible = true;
        this.errorMessage = 'Annonsen kunne ikke lastes opp, forsøk igjen om noen få minutter.';
      }
    },
    async setAdModalSelectedRentalObjectId(value) {
      this.adModalSelectedRentalObjectId = Number(value);

      if (this.adModalIsRentalObjectCommercial) {
        return;
      }

      this.isReuseAdCardLoading = true;
      this.reuseAdCardData = undefined;

      try {
        if (this.adModalSelectedRentalObjectAd) {
          this.reuseAdCardData = {
            type: 'letting',
            lettingId: this.adModalSelectedRentalObjectAd.id,
            imageUrl: this.imageUrl(this.adModalSelectedRentalObjectAd),
            header: this.adModalSelectedRentalObjectAd.content[0]?.sectionHeading,
            street: this.adModalSelectedRentalObjectAd.rentalObject.address.street,
            postalZipCode: this.adModalSelectedRentalObjectAd.rentalObject.address.postalZipCode,
            postalArea: this.adModalSelectedRentalObjectAd.rentalObject.address.postalArea,
          }
        } else {
          const api = new LandlordApi();
          const resp = await api.getRentalObjectPropCloudListings(
            this.adModalSelectedLandlordProfileId,
            this.adModalSelectedRentalObjectId,
          );

          if (Array.isArray(resp) && resp.length) {
            const listing = resp[0];

            this.reuseAdCardData = {
              type: 'propCloud',
              propCloudLettingId: listing.id,
              href: listing.link,
              imageUrl: listing.images[0]?.thumbnails?.[0]?.url,
              header: listing.heading,
              address: listing.address,
            };
          }
        }
      } finally {
        this.isReuseAdCardLoading = false;
      }
    },
  },
  async created() {
    await this.getPageData();
    this.handlePageParameter();

    await this.handleIdentificationAttempt();

    this.isViewReady = true;
  },
});
</script>

<style lang="scss" scoped>
.listAllAds {
  margin-bottom: 2rem;
}
.listAllAds__modalInput {
  display: flex;
  flex-direction: column;
  gap: 0.5rem 0;
}
.listAllAds__headerSection {
  background: #0a1f44;
  width: 100%;
  height: fit-content;
  padding: 4rem 2rem 2rem 2rem;
  color: white;
}
.listAllAds__headerSection__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem 0;
}
.listAllAds__headerSection__content__title {
  max-width: 100%;
}
.listAllAds__headerSection__content__title h1 {
  word-wrap: break-word;
}
.listAllAds__headerSection__content__buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem 0;
}
.listAllAds__content {
  display: initial;
}
.listAllAds__content__block {
  border-radius: 0;
  padding: 1.5rem;
  background: white;
  border-top: 1px solid #e9eaeb;
  border-bottom: 1px solid #e9eaeb;
  width: 100%;
}
.listAllAds__content__block__inputs {
  display: flex;
  flex-direction: column;
  gap: 1rem 0;
}
.listAllAds__content__block__inputs * {
  width: 100%;
  height: 48.6px;
}
.listAllAds__content__block__noAds {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 3rem;
  text-align: center;
}
.listAllAds__content__block__adKeyPoints {
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: 0 1rem;
}
.listAllAds__content__pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.listAllAds__content__pagination div:first-child {
  display: none;
}
select {
  font-size: 1rem;
  padding: 0.8rem 1rem;
  background-color: white;
  border: 1px solid $gray-400;
  border-radius: 8px;
  outline: 0;
  height: 48.6px;
}
select:disabled {
  background: $gray-200;
}

@media (min-width: 650px) {
  .listAllAds__modalInput {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  .listAllAds__modalInput select {
    width: 448px;
  }
  .listAllAds__headerSection {
    background: #0a1f44;
    width: 100%;
    height: 242px;
    padding: 4rem 3.6rem;
    color: white;
    height: fit-content;
  }
  .listAllAds__headerSection__content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    text-align: left;
    gap: 0;
  }
  .listAllAds__headerSection__content__title {
    max-width: 50%;
  }
  .listAllAds__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: -3rem;
  }
  .listAllAds__content__block {
    background: white;
    border: 1px solid #e9eaeb;
    border-radius: 0;
    padding: 1.5rem;
  }
  .listAllAds__content__block__inputs {
    display: flex;
    flex-direction: row;
    gap: 0 1rem;
  }
  .listAllAds__content__pagination {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
  .listAllAds__content__pagination div:first-child {
    display: initial;
  }
  .listAllAds__content__pagination span {
    display: flex;
    justify-content: center;
  }
}

@media (min-width: 900px) {
  .listAllAds__headerSection__content__buttons {
    flex-direction: row;
    gap: 0 1rem;
  }
}

@media (min-width: 1120px) {
  .listAllAds__content {
    padding: 0 3rem;
  }
  .listAllAds__content__block {
    border-radius: 12px;
  }
}
</style>
